import { useState } from 'react'
import type { FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { Card, ErrorBox } from '../components/bits'

/**
 * Secretariat sign-in.
 *
 * A separate login screen from the member app, as the handoff requires. Two
 * things worth noting:
 *
 * - **Email only.** Members may sign in with a KitNo; Secretariat staff have no
 *   KitNo, so there is nothing to resolve and no Edge Function involved here.
 * - **Being a valid Supabase user is not enough.** The app checks membership of
 *   `secretariat_users` after authenticating and signs the session straight back
 *   out if the account is a member rather than staff. Otherwise a Petarian could
 *   log in here successfully, see an empty shell, and reasonably conclude the
 *   portal was broken.
 *
 * MFA is a Supabase Auth setting (TECHNICAL-REQUIREMENTS.md §5 requires it for
 * Secretariat accounts) and is enforced there, not in this component.
 */
export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      })

      if (signInError) {
        setError(
          signInError.status === 429
            ? 'Too many attempts. Please wait a few minutes and try again.'
            : 'Those details did not match. Please try again.',
        )
        return
      }

      const userId = data.user?.id
      if (!userId) {
        setError('Sign-in failed. Please try again.')
        return
      }

      // Confirm this is a Secretariat account, not a member one.
      const { data: staff, error: staffError } = await supabase
        .from('secretariat_users')
        .select('id, name, role')
        .eq('id', userId)
        .maybeSingle()

      if (staffError || !staff) {
        await supabase.auth.signOut()
        setError(
          'This account is not a Secretariat user. Petarians should use the ' +
            'member app — this portal is for Secretariat staff only.',
        )
      }
      // On success the AuthGate in App.tsx picks up the session and swaps this
      // screen for the portal.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-card stack">
        <div style={{ textAlign: 'center' }}>
          <div className="brand-crest" style={{ margin: '0 auto 16px' }}>
            CCP
          </div>
          <div className="eyebrow">Restricted access</div>
          <h1 style={{ fontSize: 28, marginTop: 8 }}>Secretariat portal</h1>
          <p className="small" style={{ marginTop: 8 }}>
            Verification of the Petarian Alumni Network register.
          </p>
        </div>

        <Card>
          {/* `void` rather than passing the async function directly: onSubmit
              expects a void return, so React discards the promise silently and a
              rejection surfaces as an unhandled rejection rather than reaching
              the catch inside submit(). Being explicit about the discard is the
              difference between a handled error and a console warning. */}
          <form
            onSubmit={(e) => {
              void submit(e)
            }}
            className="stack"
            style={{ gap: 'var(--s-4)' }}
          >
            <div>
              <label className="kpi-label" htmlFor="email">
                Work email
              </label>
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ marginTop: 6 }}
              />
            </div>
            <div>
              <label className="kpi-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ marginTop: 6 }}
              />
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}

            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </Card>

        <p className="small muted" style={{ textAlign: 'center' }}>
          Petarians: this is not the member app. Please use the Petarian Alumni
          Network app to manage your own record.
        </p>
      </div>
    </div>
  )
}
